import React from 'react';

import { AddIcon, IconBoxButton, Input, RemoveIcon } from '@thefarmersfront/kitchen';

import { RTS_PARCEL_INVOICE_MAX_COUNT, RTS_PARCEL_INVOICE_MAX_LENGTH } from '~/constants';

import type { TypeRtsParcelInvoiceGroup } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalRtsParcelModal.module.scss';

type TypeRtsParcelInvoiceGroupProps = {
  group: TypeRtsParcelInvoiceGroup;
  /** 신청 요청이 진행 중일 때 입력·추가·삭제를 모두 잠근다 (응답을 제출 당시 입력에 그대로 반영하기 위해) */
  isDisabled?: boolean;
  onChangeInvoice: (inspectionId: number, invoiceId: string, value: string) => void;
  onBlurInvoice: () => void;
  onAddInvoice: (inspectionId: number) => void;
  onRemoveInvoice: (inspectionId: number, invoiceId: string) => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 신청 모달 - 이슈카드별 송장번호 입력 그룹
 *
 * @note 카드(이슈카드)마다 `상품코드 │ 상품명` 한 줄을 먼저 두고 그 아래에 송장번호 입력 행을 세로로 쌓는다.
 *       한 카드에 송장을 여러 개 등록할 수 있어 첫 행은 [+](추가), 이후 행은 [-](삭제) 버튼을 제공한다.
 *       송장이 최대 개수에 도달하면 [+]를 비활성화하며, 별도 에러 문구는 노출하지 않는다.
 */
const RtsParcelInvoiceGroup = ({
  group,
  isDisabled = false,
  onChangeInvoice,
  onBlurInvoice,
  onAddInvoice,
  onRemoveInvoice,
}: TypeRtsParcelInvoiceGroupProps) => {
  const isAddDisabled = isDisabled || group.invoices.length >= RTS_PARCEL_INVOICE_MAX_COUNT;

  return (
    <div className={styles['invoice-group']}>
      <div className={styles['invoice-product']}>
        <span>{group.goodsCode}</span>
        <span className={styles['invoice-product-divider']} />
        <span className={styles['invoice-product-name']}>{group.goodsName}</span>
      </div>
      {group.invoices.map((invoice, index) => {
        const isFirstRow = index === 0;

        return (
          <div key={invoice.id} className={styles['invoice-row']}>
            <div className={styles['invoice-input']}>
              <Input
                type="text"
                size="small"
                maxWidth="229px"
                placeholder="송장번호 (영문/숫자/하이픈, 8~20자)"
                maxLength={RTS_PARCEL_INVOICE_MAX_LENGTH}
                status={invoice.error ? 'error' : 'default'}
                validationMessage={invoice.error}
                disabled={isDisabled}
                value={invoice.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeInvoice(group.inspectionId, invoice.id, e.target.value)
                }
                onBlur={onBlurInvoice}
              />
            </div>
            {isFirstRow ? (
              <IconBoxButton
                size="small"
                variant="contained"
                color="secondary"
                disabled={isAddDisabled}
                onClick={() => onAddInvoice(group.inspectionId)}
              >
                <AddIcon />
              </IconBoxButton>
            ) : (
              <IconBoxButton
                size="small"
                variant="contained"
                color="secondary"
                disabled={isDisabled}
                onClick={() => onRemoveInvoice(group.inspectionId, invoice.id)}
              >
                <RemoveIcon />
              </IconBoxButton>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RtsParcelInvoiceGroup;
